import { DOCUMENT } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
} from '@angular/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatNativeDateModule } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
  ApiErrorMissingTranslationHandler,
  authErrorInterceptor,
  cspAuthErrorInterceptor,
  customLoaderFactory,
  errorLoggingInterceptor,
  forbiddenErrorInterceptor,
  suspensionInterceptor,
  tokenInterceptor,
} from '@rms/core/http';
import { provideRmsWhiteLabel } from '@rms/core/layout';
import { provideInbox } from '@rms/inbox';
import { NotificationService } from '@rms/notification';
import { provideOrganization } from '@rms/org';
import { provideRmsPaginatorIntl } from '@rms/sdk/paginator';
import { provideShop } from '@rms/shop';
import { DEFAULT_CURRENCY } from '@rms/subscription';
import { provideCustomerSupport } from '@rms/support-chat';
import { vexConfigs } from '@vex/config/vex-configs';
import { provideVex } from '@vex/vex.provider';
import { provideQuillConfig } from 'ngx-quill';
import { Mention, MentionBlot } from 'quill-mention';
import { environment } from 'src/environments/environment';

import { appRoutes } from './app.routes';
import { isChunkLoadError, promptReloadForFreshChunks } from './core/error/chunk-load-recovery';
import { GlobalErrorHandler } from './core/error/global-error-handler';
import { provideIcons } from './core/icons/icons.provider';
import { provideLanguageDetection } from './core/language-detection/language-detection.provider';
import { provideLuxon, provideRmsLuxonDateAdapter } from './core/luxon/luxon.provider';
import { provideNavigation } from './core/navigation/navigation.provider';
import { provideNotifications } from './core/notification/notification.provider';
import { WidgetsMockApiService } from './domains/widgets/services/widgets-api.mock.service';
import { WidgetsApiService } from './domains/widgets/services/widgets-api.service';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: environment.defaultLanguage },
    importProvidersFrom(
      BrowserModule,
      MatDialogModule,
      MatBottomSheetModule,
      MatNativeDateModule,
      TranslateModule.forRoot({
        fallbackLang: environment.defaultLanguage,
        loader: {
          provide: TranslateLoader,
          useFactory: customLoaderFactory,
          deps: [HttpClient],
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: ApiErrorMissingTranslationHandler,
        },
      })
    ),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        hasBackdrop: true,
        disableClose: true,
        closeOnNavigation: true,
        autoFocus: false,
      },
    },
    { provide: DEFAULT_CURRENCY_CODE, useValue: DEFAULT_CURRENCY },
    provideLanguageDetection(),
    provideRouter(
      appRoutes,
      // TODO: Add preloading withPreloading(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      // A lazy route whose chunk 404s after a deploy (stale tab) surfaces here as a
      // NavigationError, not via the global ErrorHandler — prompt the user to reload
      // for fresh bundles rather than reloading silently under them.
      withNavigationErrorHandler((navigationError) => {
        if (!isChunkLoadError(navigationError.error)) {
          return;
        }

        promptReloadForFreshChunks(inject(NotificationService), inject(DOCUMENT).defaultView);
      })
    ),
    provideNotifications(),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        tokenInterceptor,
        errorLoggingInterceptor,
        authErrorInterceptor,
        cspAuthErrorInterceptor,
        forbiddenErrorInterceptor,
        suspensionInterceptor,
      ])
    ),
    provideOrganization(),
    provideInbox(),
    provideVex({
      /**
       * The config that will be used by default.
       * This can be changed at runtime via the config panel or using the VexConfigService.
       */
      config: vexConfigs.romulus,
      /**
       * Only themes that are available in the config in tailwind.config.ts should be listed here.
       * Any theme not listed here will not be available in the config panel.
       */
      availableThemes: [
        {
          name: 'Default',
          className: 'vex-theme-default',
        },
        {
          name: 'Teal',
          className: 'vex-theme-teal',
        },
        {
          name: 'Green',
          className: 'vex-theme-green',
        },
        {
          name: 'Purple',
          className: 'vex-theme-purple',
        },
        {
          name: 'Red',
          className: 'vex-theme-red',
        },
        {
          name: 'Orange',
          className: 'vex-theme-orange',
        },
        {
          name: 'Ruber',
          className: 'vex-theme-ruber',
        },
      ],
    }),
    provideNavigation(),
    provideIcons(),
    provideLuxon(),
    provideRmsLuxonDateAdapter(),
    provideRmsPaginatorIntl(),
    provideRmsWhiteLabel(),
    provideShop(),
    provideQuillConfig({
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['clean'],
          ['link', 'image'],
        ],
      },
      customModules: [
        {
          implementation: Mention,
          path: 'modules/mention',
        },
        {
          implementation: MentionBlot,
          path: 'blots/mention',
        },
      ],
    }),
    ...provideCustomerSupport(),
    // Registered after importProvidersFrom(BrowserModule) so it overrides BrowserModule's default ErrorHandler.
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // TODO: remove when widgets API is ready
    { provide: WidgetsApiService, useClass: WidgetsMockApiService },
  ],
};
